import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Users,
  EnvelopeSimple,
  PaperPlaneTilt,
  Eye,
  ShieldCheck,
  Warning,
  CheckCircle,
  ClockCounterClockwise,
  ArrowsClockwise,
} from "@phosphor-icons/react";

const DEFAULT_FILTERS = {
  statuses: ["pending_confirmation"],
  mode: null,
  since: null,
  until: null,
  exclude_already_sent: true,
  campaign: "launch_v1",
};

export default function EarlySupportersPanel({ adminEmail = "" }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [audience, setAudience] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);

  const [subject, setSubject] = useState("");
  const [previewTo, setPreviewTo] = useState(adminEmail);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [testTo, setTestTo] = useState(adminEmail);
  const [testSending, setTestSending] = useState(false);

  const [confirmText, setConfirmText] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const [campaigns, setCampaigns] = useState([]);

  const loadAudience = async (next = filters) => {
    setAudienceLoading(true);
    try {
      const res = await api.post("/admin/supporters/audience", next);
      setAudience(res.data);
      if (!subject) setSubject(res.data.subjects?.[0] || "");
    } catch (e) {
      toast.error("Couldn't load audience");
    } finally {
      setAudienceLoading(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.post("/admin/supporters/preview", {
        subject,
        sample_email: previewTo || undefined,
        campaign: filters.campaign,
      });
      setPreview(res.data);
    } catch {
      toast.error("Couldn't render preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res = await api.get("/admin/supporters/campaigns");
      setCampaigns(res.data.items || []);
    } catch {/* non-fatal */}
  };

  useEffect(() => { loadAudience(); loadCampaigns(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { setPreviewTo(adminEmail); setTestTo(adminEmail); }, [adminEmail]);

  const handleTestSend = async () => {
    if (!testTo) { toast.error("Enter a recipient email"); return; }
    setTestSending(true);
    try {
      const res = await api.post("/admin/supporters/test-send", {
        to: testTo, subject, campaign: filters.campaign,
      });
      if (res.data.ok) {
        toast.success(`Test sent to ${testTo}`);
      } else if (res.data.skipped_reason === "provider_unconfigured") {
        toast.error("RESEND_API_KEY not set on server. Add it to backend/.env and restart.");
      } else {
        toast.error(`Test failed: ${res.data.error || "unknown"}`);
      }
    } catch (e) {
      toast.error("Test send failed");
    } finally {
      setTestSending(false);
    }
  };

  const expectedConfirm = String(audience?.count ?? "");
  const canSend = !!audience?.count && confirmText === expectedConfirm && !bulkSending;

  const handleBulkSend = async (dryRun) => {
    if (!audience?.count) { toast.error("Audience is empty"); return; }
    if (!dryRun && confirmText !== expectedConfirm) {
      toast.error(`Type ${expectedConfirm} to confirm sending to ${expectedConfirm} supporters`);
      return;
    }
    setBulkSending(true);
    try {
      const res = await api.post("/admin/supporters/send", {
        filters, subject, campaign: filters.campaign, confirm_count: audience.count, dry_run: dryRun,
      });
      setLastResult(res.data);
      toast.success(
        dryRun
          ? `Dry run complete — ${res.data.sent} would have been sent`
          : `Sent ${res.data.sent}/${res.data.attempted} (failed ${res.data.failed}, skipped ${res.data.skipped})`
      );
      setConfirmText("");
      loadAudience();
      loadCampaigns();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Send failed";
      toast.error(detail);
    } finally {
      setBulkSending(false);
    }
  };

  const subjects = audience?.subjects || ["Giving is now live on Voice"];
  const providerOk = audience?.provider_configured;

  const badge = useMemo(() => providerOk
    ? { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle, label: "Resend connected" }
    : { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: Warning, label: "RESEND_API_KEY missing" },
    [providerOk]
  );

  return (
    <div className="space-y-5" data-testid="supporters-panel">
      {/* Header / status */}
      <div className="border border-slate-200 rounded-xl bg-white p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={22} weight="duotone" className="text-[#C4A265]" />
            <div>
              <h3 className="font-heading text-lg text-slate-900">Early Supporter Reactivation</h3>
              <p className="text-xs text-slate-500">Notify pledgers the moment giving goes live.</p>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${badge.cls}`} data-testid="provider-status">
            <badge.icon size={12} weight="fill" /> {badge.label}
          </span>
        </div>
        {!providerOk && (
          <p className="text-[12px] text-amber-800 mt-3 leading-relaxed bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            Add <code className="bg-amber-100 px-1 rounded">RESEND_API_KEY</code> and <code className="bg-amber-100 px-1 rounded">RESEND_FROM_EMAIL</code> to <code className="bg-amber-100 px-1 rounded">/app/backend/.env</code>, then restart the backend. Dry-run still works without a key.
          </p>
        )}
      </div>

      {/* Audience filters */}
      <div className="border border-slate-200 rounded-xl bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users size={18} weight="duotone" className="text-[#C4A265]" />
            <h4 className="font-heading text-sm text-slate-900">Audience</h4>
          </div>
          <button
            onClick={() => loadAudience()}
            disabled={audienceLoading}
            data-testid="audience-refresh-btn"
            className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <ArrowsClockwise size={13} className={audienceLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <label className="text-xs text-slate-600">
            Mode
            <select
              data-testid="filter-mode"
              value={filters.mode || ""}
              onChange={(e) => { const v = e.target.value || null; const f = { ...filters, mode: v }; setFilters(f); loadAudience(f); }}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
            >
              <option value="">Any</option>
              <option value="monthly">Monthly</option>
              <option value="one-time">One-time</option>
            </select>
          </label>
          <label className="text-xs text-slate-600">
            Pledged since
            <input
              data-testid="filter-since"
              type="date"
              value={filters.since ? filters.since.slice(0,10) : ""}
              onChange={(e) => { const v = e.target.value ? new Date(e.target.value).toISOString() : null; const f = { ...filters, since: v }; setFilters(f); loadAudience(f); }}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
            />
          </label>
          <label className="text-xs text-slate-600 inline-flex items-center gap-2 mt-5">
            <input
              data-testid="filter-exclude-sent"
              type="checkbox"
              checked={filters.exclude_already_sent}
              onChange={(e) => { const f = { ...filters, exclude_already_sent: e.target.checked }; setFilters(f); loadAudience(f); }}
              className="accent-[#C4A265]"
            />
            Exclude already emailed for this campaign
          </label>
        </div>

        <div className="rounded-lg bg-slate-50 p-4 flex items-baseline gap-3 flex-wrap" data-testid="audience-summary">
          <span className="text-3xl font-heading text-slate-900">{audience?.count ?? "—"}</span>
          <span className="text-sm text-slate-600">supporters match</span>
          <span className="text-xs text-slate-400 ml-2">campaign · {filters.campaign}</span>
        </div>

        {audience?.sample?.length > 0 && (
          <div className="mt-3 max-h-44 overflow-auto border border-slate-100 rounded-md text-xs" data-testid="audience-sample">
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-slate-500 text-[10px] uppercase tracking-wider">
                  <th className="text-left px-3 py-1.5">Email</th>
                  <th className="text-left px-3 py-1.5">Amount</th>
                  <th className="text-left px-3 py-1.5">Mode</th>
                </tr>
              </thead>
              <tbody>
                {audience.sample.map((s, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 text-slate-700">{s.user_email}</td>
                    <td className="px-3 py-1.5 text-slate-700">${s.amount_usd}</td>
                    <td className="px-3 py-1.5 text-slate-500">{s.mode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-slate-400 px-3 py-1.5">Showing first 10 of {audience.count}.</p>
          </div>
        )}
      </div>

      {/* Subject + preview */}
      <div className="border border-slate-200 rounded-xl bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Eye size={18} weight="duotone" className="text-[#C4A265]" />
          <h4 className="font-heading text-sm text-slate-900">Email content</h4>
        </div>

        <label className="text-xs text-slate-600 block mb-3">
          Subject
          <select
            data-testid="subject-select"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
          >
            {subjects.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>

        <div className="flex items-end gap-2 mb-3 flex-wrap">
          <label className="text-xs text-slate-600 flex-1">
            Preview as
            <input
              data-testid="preview-to"
              value={previewTo}
              onChange={(e) => setPreviewTo(e.target.value)}
              placeholder="supporter@example.com"
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
            />
          </label>
          <button
            onClick={loadPreview}
            disabled={previewLoading}
            data-testid="render-preview-btn"
            className="px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-60"
          >
            {previewLoading ? "Rendering…" : "Render preview"}
          </button>
        </div>

        {preview && (
          <div className="space-y-2" data-testid="preview-content">
            <p className="text-[11px] text-slate-500"><b>From:</b> {preview.from} · <b>To:</b> {preview.to} · <b>Subject:</b> {preview.subject}</p>
            <iframe
              title="email-preview"
              srcDoc={preview.html}
              data-testid="preview-iframe"
              className="w-full h-[500px] rounded-md border border-slate-200 bg-white"
            />
            <details className="text-xs text-slate-600">
              <summary className="cursor-pointer">Plain-text fallback</summary>
              <pre className="mt-2 bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap text-[12px] leading-relaxed">{preview.text}</pre>
            </details>
          </div>
        )}
      </div>

      {/* Test send */}
      <div className="border border-slate-200 rounded-xl bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <PaperPlaneTilt size={18} weight="duotone" className="text-[#C4A265]" />
          <h4 className="font-heading text-sm text-slate-900">Send a test</h4>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs text-slate-600 flex-1 min-w-[220px]">
            Recipient
            <input
              data-testid="test-to"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@yourdomain.com"
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
            />
          </label>
          <button
            onClick={handleTestSend}
            disabled={testSending || !testTo}
            data-testid="test-send-btn"
            className="px-4 py-2 rounded-full bg-[#2D1B0E] text-white text-xs font-medium hover:bg-[#3D2B1F] disabled:opacity-60"
          >
            {testSending ? "Sending…" : "Send test"}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Test sends are not recorded against the campaign — that recipient is still eligible for the real send.
        </p>
      </div>

      {/* Bulk send */}
      <div className="border border-slate-200 rounded-xl bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={18} weight="duotone" className="text-amber-700" />
          <h4 className="font-heading text-sm text-slate-900">Send to audience</h4>
        </div>

        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 mb-3 text-[12px] text-amber-900 leading-relaxed flex gap-2">
          <Warning size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            This sends a real email to <b>{audience?.count ?? "—"}</b> early supporters.
            Type the audience size below to confirm. Use <b>Dry run</b> first to validate.
          </span>
        </div>

        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs text-slate-600">
            Confirm by typing <b className="text-slate-900">{expectedConfirm || "—"}</b>
            <input
              data-testid="confirm-text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expectedConfirm}
              className="mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm bg-white w-32"
            />
          </label>
          <button
            onClick={() => handleBulkSend(true)}
            disabled={bulkSending || !audience?.count}
            data-testid="dry-run-btn"
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-xs font-medium hover:bg-slate-50 disabled:opacity-60"
          >
            {bulkSending ? "Working…" : "Dry run"}
          </button>
          <button
            onClick={() => handleBulkSend(false)}
            disabled={!canSend || !providerOk}
            data-testid="bulk-send-btn"
            className="px-5 py-2 rounded-full bg-[#2D1B0E] text-white text-xs font-medium hover:bg-[#3D2B1F] disabled:opacity-50 disabled:cursor-not-allowed"
            title={!providerOk ? "Set RESEND_API_KEY first" : ""}
          >
            {bulkSending ? "Sending…" : `Send to ${audience?.count ?? 0} supporters`}
          </button>
        </div>

        {lastResult && (
          <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-3 text-xs" data-testid="last-result">
            <p className="text-slate-700">
              {lastResult.dry_run ? "Dry run · " : "Live send · "}
              <b>{lastResult.sent}</b> sent ·{" "}
              <span className="text-rose-700">{lastResult.failed}</span> failed ·{" "}
              <span className="text-slate-500">{lastResult.skipped}</span> skipped ·{" "}
              of {lastResult.attempted} attempted ({lastResult.audience_size} in audience)
            </p>
          </div>
        )}
      </div>

      {/* Campaign history */}
      <div className="border border-slate-200 rounded-xl bg-white p-5" data-testid="campaign-history">
        <div className="flex items-center gap-2 mb-3">
          <ClockCounterClockwise size={18} weight="duotone" className="text-[#C4A265]" />
          <h4 className="font-heading text-sm text-slate-900">Recent runs</h4>
        </div>
        {campaigns.length === 0 ? (
          <p className="text-xs text-slate-500">No campaigns sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="text-left py-1.5 pr-3">When</th>
                  <th className="text-left py-1.5 pr-3">Campaign</th>
                  <th className="text-left py-1.5 pr-3">Subject</th>
                  <th className="text-left py-1.5 pr-3">Mode</th>
                  <th className="text-right py-1.5 pr-3">Sent</th>
                  <th className="text-right py-1.5 pr-3">Failed</th>
                  <th className="text-right py-1.5">Skipped</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-600">{new Date(c.started_at).toLocaleString()}</td>
                    <td className="py-1.5 pr-3 text-slate-700">{c.slug}</td>
                    <td className="py-1.5 pr-3 text-slate-700 truncate max-w-[220px]">{c.subject}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{c.dry_run ? "dry" : "live"}</td>
                    <td className="py-1.5 pr-3 text-right">{c.sent}</td>
                    <td className="py-1.5 pr-3 text-right text-rose-700">{c.failed}</td>
                    <td className="py-1.5 text-right text-slate-500">{c.skipped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
